import { create } from 'zustand';

/**
 * Authenticated agent — the subject of the JWT issued by the Hub.
 *
 * Mirrors the `AgentOut` payload of the Hub's `/api/v1/auth/login` and
 * `/api/v1/auth/me`: the four columns the console renders, with the password
 * hash stripped. `role` is `'agent' | 'admin'` (server-defined enum).
 */
export interface AgentSummary {
  id: string;
  email: string;
  display_name: string;
  role: 'agent' | 'admin';
}

interface AuthState {
  /** Bearer JWT — `null` when signed out. The single source of truth for the
   *  axios request interceptor is `localStorage`; this field mirrors it for
   *  the rare React component that wants to read it synchronously. */
  accessToken: string | null;
  /** The agent the bearer token belongs to, or `null` when signed out.
   *  `useCurrentAgent` is the canonical fetcher; this field is the cached
   *  copy other code can read without bouncing through React Query. */
  agent: AgentSummary | null;
  /** Persist a successful login: write token + agent into localStorage and
   *  mirror them into the store. Called by LoginPage on success and by
   *  `useCurrentAgent` on initial /auth/me success. */
  setSession: (session: { accessToken: string; agent: AgentSummary }) => void;
  /** Wipe the session — remove the localStorage keys and zero out the store.
   *  Idempotent; safe to call from the 401 interceptor and the boundary. */
  clear: () => void;
}

/** localStorage key holding the JWT access token. Read by the axios request
 *  interceptor; written/cleared by the auth store. The interceptor cannot use
 *  the Zustand store (it runs outside React's render lifecycle), so the
 *  localStorage value is the source of truth across reloads and across tabs. */
export const ACCESS_TOKEN_STORAGE_KEY = 'icgchat.access_token';

/** localStorage key holding a paint-without-flicker copy of the agent profile.
 *  /auth/me remains authoritative; this is only the cache for the initial
 *  render so we don't blank the UI for a roundtrip on every refresh. */
export const AGENT_STORAGE_KEY = 'icgchat.agent';

/**
 * Hydrate the in-memory store from localStorage. Runs once at module load so a
 * hard reload of the SPA keeps the agent signed in (subject to /auth/me
 * confirming the token is still valid — the boundary handles that).
 */
function readHydratedState(): {
  accessToken: string | null;
  agent: AgentSummary | null;
} {
  if (typeof window === 'undefined') {
    return { accessToken: null, agent: null };
  }
  const accessToken = window.localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);
  let agent: AgentSummary | null = null;
  const raw = window.localStorage.getItem(AGENT_STORAGE_KEY);
  if (raw) {
    try {
      const parsed = JSON.parse(raw) as AgentSummary;
      if (
        parsed &&
        typeof parsed.id === 'string' &&
        typeof parsed.email === 'string' &&
        typeof parsed.display_name === 'string' &&
        (parsed.role === 'agent' || parsed.role === 'admin')
      ) {
        agent = parsed;
      }
    } catch {
      // Tampered/corrupt cache — ignore and fall back to /auth/me.
    }
  }
  return { accessToken, agent };
}

export const useAuthStore = create<AuthState>((set) => {
  const initial = readHydratedState();
  return {
    accessToken: initial.accessToken,
    agent: initial.agent,
    setSession: ({ accessToken, agent }) => {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, accessToken);
        window.localStorage.setItem(AGENT_STORAGE_KEY, JSON.stringify(agent));
      }
      set({ accessToken, agent });
    },
    clear: () => {
      if (typeof window !== 'undefined') {
        window.localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
        window.localStorage.removeItem(AGENT_STORAGE_KEY);
      }
      set({ accessToken: null, agent: null });
    },
  };
});
